import { Injectable } from '@angular/core';
import { MeasurementDTO } from '@core/apis/measurement/measurement.dto';
import { IMeasurementEcg } from '@core/apis/measurement/measurement.interface';

@Injectable({
  providedIn: 'root',
})
export class CsvEcgMeasurementService {
  viewResults = [
    'results',
    'lead',
    'timeAnalysis',
    'spectralAnalysis',
    'summary',
    'id',
    'patientId',
    'staffId',
    'clinicId',
    'registrationDate',
    'requestedDate',
    'computedDate',
    'type',
    'status',
    'fs',
    'errorStatus',
    'bestLeadForAnalysis',
  ];

  convertToCSV(dataArray: MeasurementDTO[]): string {
    const rows: string[] = [];
    const parameterSet = new Set<string>();
    const descriptionSet = new Set<string>();
    const recordsInfoSet = new Set<string>();
    const leadSet = new Set<string>();

    const leadDataMap: Record<string, string[][]> = {};

    dataArray.forEach((data: MeasurementDTO) => {
      const ecgRecords = data?.['records'] as IMeasurementEcg;
      if (!ecgRecords || !ecgRecords.leads) return;

      const flatDescription = this.flattenObject(data);
      Object.keys(flatDescription).forEach((key) => descriptionSet.add(key));

      const flatRecordsInfo = this.flattenObject({
        fs: ecgRecords.fs,
        errorStatus: ecgRecords.errorStatus,
        bestLeadForAnalysis: ecgRecords.bestLeadForAnalysis,
      });
      Object.keys(flatRecordsInfo).forEach((key) => recordsInfoSet.add(key));

      leadSet.add('record');
      leadSet.add('lead');

      ecgRecords.leads.forEach((leadData: any) => {
        const flat = this.flattenObject(leadData);
        Object.keys(flat).forEach((key) => {
          parameterSet.add(key);
        });
      });
    });

    const parameterKeys = Array.from(parameterSet).sort();
    const descriptionKeys = Array.from(descriptionSet).sort();
    const recordsInfoKeys = Array.from(recordsInfoSet).sort();
    const leadKeys = Array.from(leadSet);

    const timeAnalysisKeys = parameterKeys.filter((k) => k.startsWith('results.timeAnalysis.'));

    const spectralAnalysisFftRrIntervalKeys = parameterKeys.filter((k) =>
      k.startsWith('results.spectralAnalysis.fftRrInterval.'),
    );
    const spectralAnalysisFftSsIntervalKeys = parameterKeys.filter((k) =>
      k.startsWith('results.spectralAnalysis.fftSsInterval.'),
    );
    const spectralAnalysisHilbertHuangRrIntervalKeys = parameterKeys.filter((k) =>
      k.startsWith('results.spectralAnalysis.hilbertHuangRrInterval.'),
    );
    const spectralAnalysisHilbertHuangSsIntervalKeys = parameterKeys.filter((k) =>
      k.startsWith('results.spectralAnalysis.hilbertHuangSsInterval.'),
    );
    const spectralAnalysisFftKeys = parameterKeys.filter(
      (k) =>
        k.startsWith('results.spectralAnalysis.fft.') &&
        !k.startsWith('results.spectralAnalysis.fftRrInterval.') &&
        !k.startsWith('results.spectralAnalysis.fftSsInterval.'),
    );
    const spectralAnalysisHilbertHuangKeys = parameterKeys.filter(
      (k) =>
        k.startsWith('results.spectralAnalysis.hilbertHuang.') &&
        !k.startsWith('results.spectralAnalysis.hilbertHuangRrInterval.') &&
        !k.startsWith('results.spectralAnalysis.hilbertHuangSsInterval.'),
    );

    const summaryKeys = parameterKeys.filter((k) => k.startsWith('results.summary.'));
    const otherKeys = parameterKeys.filter(
      (k) =>
        !k.startsWith('results.timeAnalysis.') &&
        !k.startsWith('results.spectralAnalysis.') &&
        !k.startsWith('results.summary.'),
    );

    const headers = [
      ...descriptionKeys,
      ...recordsInfoKeys,
      ...leadKeys,
      ...timeAnalysisKeys.map((k) => k.split('.').pop()!),
      ...spectralAnalysisFftKeys.map((k) => k.split('.').pop()!),
      ...spectralAnalysisHilbertHuangKeys.map((k) => k.split('.').pop()!),
      ...spectralAnalysisFftRrIntervalKeys.map((k) => k.split('.').pop()!),
      ...spectralAnalysisFftSsIntervalKeys.map((k) => k.split('.').pop()!),
      ...spectralAnalysisHilbertHuangRrIntervalKeys.map((k) => k.split('.').pop()!),
      ...spectralAnalysisHilbertHuangSsIntervalKeys.map((k) => k.split('.').pop()!),
      ...summaryKeys.map((k) => k.split('.').pop()!),
      ...otherKeys.map((k) => k.split('.').pop()!),
    ];

    const keyMapping: Record<string, string> = {};
    parameterKeys.forEach((key) => {
      keyMapping[key] = key.split('.').pop()!;
    });

    const sectionRow: string[] = [];

    sectionRow.push('measurement');
    for (let i = 1; i < descriptionKeys.length; i++) {
      sectionRow.push('');
    }

    sectionRow.push('records');
    for (let i = 1; i < recordsInfoKeys.length; i++) {
      sectionRow.push('');
    }

    sectionRow.push('leadInfo');
    for (let i = 1; i < leadKeys.length; i++) {
      sectionRow.push('');
    }

    if (timeAnalysisKeys.length > 0) {
      sectionRow.push('timeAnalysis');
      for (let i = 1; i < timeAnalysisKeys.length; i++) {
        sectionRow.push('');
      }
    }

    if (spectralAnalysisFftKeys.length > 0) {
      sectionRow.push('fft');
      for (let i = 1; i < spectralAnalysisFftKeys.length; i++) {
        sectionRow.push('');
      }
    }

    if (spectralAnalysisHilbertHuangKeys.length > 0) {
      sectionRow.push('hilbertHuang');
      for (let i = 1; i < spectralAnalysisHilbertHuangKeys.length; i++) {
        sectionRow.push('');
      }
    }

    if (spectralAnalysisFftRrIntervalKeys.length > 0) {
      sectionRow.push('fftRrInterval');
      for (let i = 1; i < spectralAnalysisFftRrIntervalKeys.length; i++) {
        sectionRow.push('');
      }
    }

    if (spectralAnalysisFftSsIntervalKeys.length > 0) {
      sectionRow.push('fftSsInterval');
      for (let i = 1; i < spectralAnalysisFftSsIntervalKeys.length; i++) {
        sectionRow.push('');
      }
    }

    if (spectralAnalysisHilbertHuangRrIntervalKeys.length > 0) {
      sectionRow.push('hilbertHuangRrInterval');
      for (let i = 1; i < spectralAnalysisHilbertHuangRrIntervalKeys.length; i++) {
        sectionRow.push('');
      }
    }

    if (spectralAnalysisHilbertHuangSsIntervalKeys.length > 0) {
      sectionRow.push('hilbertHuangSsInterval');
      for (let i = 1; i < spectralAnalysisHilbertHuangSsIntervalKeys.length; i++) {
        sectionRow.push('');
      }
    }

    if (summaryKeys.length > 0) {
      sectionRow.push('summary');
      for (let i = 1; i < summaryKeys.length; i++) {
        sectionRow.push('');
      }
    }

    for (let i = 0; i < otherKeys.length; i++) {
      sectionRow.push('');
    }

    rows.push(sectionRow.join(','));
    rows.push(headers.join(','));

    dataArray.forEach((data: MeasurementDTO, index: number) => {
      const ecgRecords = data?.['records'] as IMeasurementEcg;
      if (!ecgRecords || !ecgRecords.leads) return;

      const recordsInfo = {
        fs: ecgRecords.fs,
        errorStatus: ecgRecords.errorStatus,
        bestLeadForAnalysis: ecgRecords.bestLeadForAnalysis,
      };

      ecgRecords.leads.forEach((leadData, leadIndex) => {
        const flatParams = this.flattenObject(leadData);

        const mappedParams: any = {};
        Object.keys(flatParams).forEach((key) => {
          const newKey = keyMapping[key] || key;
          mappedParams[newKey] = flatParams[key];
        });

        const row: any = {
          ...(leadIndex === 0
            ? this.flattenObject(data)
            : this.getEmptyDescription(Object.keys(this.flattenObject(data)))),
          ...(leadIndex === 0 ? this.flattenObject(recordsInfo) : this.getEmptyDescription(Object.keys(recordsInfo))),
          record: index + 1,
          lead: leadData.lead,
          ...mappedParams,
        };

        const values = headers.map((h) => (row[h] === null || row[h] === undefined ? '' : row[h]));
        rows.push(values.join(','));

        const lead = leadData.lead;
        if (!leadDataMap[lead]) {
          leadDataMap[lead] = [];
        }
        leadDataMap[lead].push(values);
      });

      rows.push('');
    });

    const descriptionAndRecordsInfoLength = descriptionKeys.length + recordsInfoKeys.length + 2;

    Object.keys(leadDataMap).forEach((lead) => {
      const stdRow = headers.map((h, colIndex) => {
        if (colIndex < descriptionAndRecordsInfoLength) return '';

        const columnValues = leadDataMap[lead].map((row) => parseFloat(row[colIndex])).filter((v) => !isNaN(v));

        if (columnValues.length === 0) return 'NaN±NaN';

        const mean = columnValues.reduce((sum, v) => sum + v, 0) / columnValues.length;
        const variance = columnValues.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / columnValues.length;
        const standardError = Math.sqrt(variance / columnValues.length);
        return `${mean.toFixed(2)}±${standardError.toFixed(2)}`;
      });

      stdRow[0] = `M±m (${lead})`;
      rows.push(stdRow.join(','));
    });

    return rows.join('\n');
  }

  private flattenObject(obj: any, prefix = ''): Record<string, any> {
    const res: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const hasIntersection = fullKey.split('.').some((item) => this.viewResults.includes(item));

      if (hasIntersection) {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          Object.assign(res, this.flattenObject(value, fullKey));
        } else {
          res[fullKey] = value;
        }
      }
    }
    return res;
  }

  private getEmptyDescription(keys: string[]): Record<string, null> {
    return keys.reduce((acc, key) => ({ ...acc, [key]: null }), {});
  }
}
